import { Component, Input, OnInit } from '@angular/core';
import _ from 'lodash';
import { FlowType } from 'src/app/models/proofreading.model';
import { ProofreadingService } from 'src/app/services/proofreading.service';
import { ProjectService } from 'src/app/services/project.service';
import { IVersionSelector, ProofreadingHistoryService } from 'src/app/services/proofreading-history.service';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/services/user.service';
import { ViewComponent } from 'src/app/shared/pages/view/view.component';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from 'src/app/services/alert.service';
import { LoaderService } from 'src/app/services/loader.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IProofreadingMember, ProofreadingMember } from 'src/app/models/proofreading-member.model';
import { StateMachineService } from 'src/app/services/machine-state.service';


interface IThumbnail {
    src: string;
}
@Component({
    selector: 'app-history-detail',
    templateUrl: './history-detail.component.html',
    styleUrls: ['./history-detail.component.scss'],
})
export class HistoryDetailComponent extends ViewComponent implements OnInit {
    protected _version: BehaviorSubject<IVersionSelector | null | undefined> = new BehaviorSubject<IVersionSelector | null | undefined>(undefined);
    @Input() set version(value: IVersionSelector | null | undefined) {
        this._version.next(value);
    }
    get version() {
        return this._version.value;
    }

    public externalTab: "extern" | "approval" = this.userHasInternRole() ? "extern" : 'approval';

    get selectedVersion(): IVersionSelector | null {
        if (this.version !== undefined) return this.version;
        return this.s_proofreadingHistory.selectedVersion;
    };

    get projectHistory() {
        return this.s_proofreadingHistory.projectHistories
    }

    get project() {
        return this.s_project.current;
    }

    get isInternal() {
        return this.selectedVersion?.version !== undefined;
    }

    get historyUsersID() {
        let ids = [];
        if (this.s_auth.hasRole(['ADMIN', 'CPP', 'FAB', 'OPERATOR']) && this.isInternal) {
            ids = _.uniq(this.selectedHistoryIntern?.states.map(s => s.user));
        } else {
            if (this.s_auth.hasRole(['ADMIN', 'CPP', 'FAB', 'OPERATOR'])) {
                ids = _.uniq([...this.selectedHistoryExtern?.map(record => record.user) || [], ...this.selectedHistoryApproval?.members.map((s: any) => s._id) || []]);
            } else {
                ids = _.uniq(this.selectedHistoryApproval?.members.map((s: any) => s._id) || []);
            }
        }

        return ids.filter(id => !!id);
    }

    get selectedHistoryIntern() {
        if (!this.selectedVersion || !this.projectHistory) {
            return null;
        }

        const selectedFlow = this.projectHistory?.find(h => h.id === this.selectedVersion!.id);

        if (!selectedFlow?.intern || !this.isInternal) {
            return null;
        }

        const relectureTag = (this.getPrefixRelecture(this.selectedVersion!.flow).short + this.selectedVersion.relecture) as string;

        const internHistory = selectedFlow.intern;
        const selectedRelecture = internHistory.history[relectureTag] || null;

        if (!selectedRelecture) {
            return null;
        }

        return selectedRelecture[this.selectedVersion.version!] || null;

    }

    get selectedHistoryExtern() {
        if (!this.selectedVersion || !this.projectHistory) {
            return null;
        }

        const selectedFlow = this.projectHistory?.find(h => h.id === this.selectedVersion!.id);


        if (!selectedFlow?.extern) {
            return null;
        }

        const relectureTag = (this.getPrefixRelecture(this.selectedVersion!.flow).short + this.selectedVersion.relecture) as string;

        const externHistory = selectedFlow.extern;
        const selectedRelecture = externHistory.history[relectureTag] || null;

        return selectedRelecture || null;
    }

    get selectedHistoryApproval() {
        if (!this.selectedVersion || !this.projectHistory) {
            return null;
        }

        const selectedFlow = this.projectHistory?.find(h => h.id === this.selectedVersion!.id);

        if (!selectedFlow?.approval) {
            return null;
        }

        const relectureTag = (this.getPrefixRelecture(this.selectedVersion!.flow).short + this.selectedVersion.relecture) as string;

        const approHistory = selectedFlow.approval;
        const selectedRelecture = approHistory.history[relectureTag] || null;

        return selectedRelecture || null;
    }

    constructor(
        protected s_router: Router,
        protected s_route: ActivatedRoute,
        protected s_alert: AlertService,
        protected s_loader: LoaderService,
        protected s_proofreadingHistory: ProofreadingHistoryService,
        protected s_project: ProjectService,
        protected s_auth: AuthService,
        protected s_user: UsersService,
        
        protected s_sm: StateMachineService
    ) {
        super(s_route, s_router, s_alert, s_loader, s_auth);
    }

    async ngOnInit(): Promise<void> {
        this.subscription.add(
            combineLatest([this._version, this.s_proofreadingHistory.selectedVersion$]).subscribe(async (value) => {
                if (!this.historyUsersID.length) return;
                await this.s_user.find({ '_id': { $in: this.historyUsersID } }, { $pushToEntityList: true });
            })
        );
    }

    getPrefixRelecture(flow: FlowType) {
        return ProofreadingService.getPrefixRelecture(flow);
    }

    ObjectKeys(obj: any) {
        return Object.keys(obj);
    }

    userHasInternRole(): boolean {
        return this.s_auth.hasRole(['ADMIN', 'CPP', 'FAB', 'OPERATOR']);
    }

    getUser(userId: string) {
        return this.s_user.local.resolve(userId);
    }

    parseMembers(members: IProofreadingMember[]) {
        return members.map(member => {
            return new ProofreadingMember(member);
        });
    }

    getSlugText(next: string): string {
        const selectedFlow = this.projectHistory?.find(h => h.id === this.selectedVersion!.id);

        const next_state = this.s_sm.entities.find(e => e.slug === next && e.type === (this.isInternal ? 'intern' : 'extern') && e.flow.includes(selectedFlow?.flow || FlowType.EXE));
        return next_state?.payload?.text_slug || next;
    }
}